import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{!-- «UI nuova» language dropdown with flags (NvLangMenu, owner 2026-10-19). Styles: beebee-base\n     app/styles/nuova/_lang-menu.scss. The rows stay rendered while closed (hidden list) so the hosts\' hooks resolve. --}}\n<div\n  class=\'nv-lang-menu {{if this.isOpen \"is-open\"}} {{if @inline \"nv-lang-menu--inline\"}}\'\n  data-ui-part=\'lang\'\n  ...attributes\n  {{did-insert this.setRoot}}\n>\n  <button\n    type=\'button\'\n    class=\'nv-lang-menu-trigger\'\n    aria-haspopup=\'menu\'\n    aria-expanded={{if this.isOpen \'true\' \'false\'}}\n    aria-controls={{this.menuId}}\n    aria-label={{this.triggerLabel}}\n    aria-busy={{if @busy \'true\'}}\n    disabled={{@busy}}\n    data-test-nv-lang-trigger\n    {{on \'click\' this.toggle}}\n    {{on \'keydown\' this.onTriggerKey}}\n  >\n    {{#if this.currentItem.flagUrl}}\n      <img class=\'nv-lang-menu-flag\' src={{this.currentItem.flagUrl}} width=\'20\' height=\'15\' alt=\'\' />\n    {{else}}\n      <FaIcon @icon=\'globe\' class=\'nv-lang-menu-globe\' aria-hidden=\'true\' />\n    {{/if}}\n    {{#if this.currentItem}}\n      <span class=\'nv-lang-menu-code\' aria-hidden=\'true\'>{{this.currentItem.short}}</span>\n    {{/if}}\n    <FaIcon\n      @icon={{if @busy \'circle-notch\' \'chevron-down\'}}\n      @spin={{@busy}}\n      class=\'nv-lang-menu-chev\'\n      aria-hidden=\'true\'\n    />\n  </button>\n\n  <div\n    id={{this.menuId}}\n    class=\'nv-lang-menu-pop\'\n    role=\'menu\'\n    aria-label={{@label}}\n    hidden={{unless this.isOpen true}}\n    {{on \'keydown\' this.onMenuKey}}\n  >\n    {{#each this.items key=\'code\' as |item|}}\n      <button\n        type=\'button\'\n        class=\'nv-lang-menu-item\'\n        role=\'menuitemradio\'\n        aria-checked={{if item.isCurrent \'true\' \'false\'}}\n        tabindex=\'-1\'\n        lang={{item.code}}\n        data-test-nv-lang-item={{item.code}}\n        {{did-insert (fn this.markItem item)}}\n        {{on \'click\' (fn this.pick item.code)}}\n      >\n        {{#if item.flagUrl}}\n          <img class=\'nv-lang-menu-flag\' src={{item.flagUrl}} width=\'20\' height=\'15\' alt=\'\' />\n        {{/if}}\n        <span class=\'nv-lang-menu-name\'>{{item.name}}</span>\n        {{#if item.isCurrent}}\n          <FaIcon @icon=\'check\' class=\'nv-lang-menu-ck\' aria-hidden=\'true\' />\n        {{/if}}\n      </button>\n    {{/each}}\n  </div>\n</div>\n");

var _class, _descriptor, _descriptor2;
const OUTSIDE_EVENTS = ['pointerdown', 'mousedown'];
const FLAG_BASE = 'https://s3.eu-central-1.amazonaws.com/scuolesci-ecommerce.beebeeboard.com/assets/img/flag/';

// Native names (the visible text is the language's own name, as the classic aria-labels) and the classic flag files.
const LANGS = {
  it: {
    name: 'Italiano',
    flag: 'italiano'
  },
  en: {
    name: 'English',
    flag: 'inglese'
  },
  de: {
    name: 'Deutsch',
    flag: 'tedesco'
  },
  fr: {
    name: 'Français',
    flag: 'francese'
  }
};
let NvLangMenuComponent = (_class = class NvLangMenuComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "isOpen", _descriptor, this);
    this.menuId = `nv-lang-menu-${guidFor(this)}`;
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    this._root = null;
    this._onOutside = null;
  }
  get items() {
    // a host whose language was never picked passes nothing (booking `lang` is unset until a click): fall back to
    // the locale intl is rendering, or no row is ever marked current and the trigger shows no flag
    const current = String(this.args.current || this.intl?.primaryLocale || '').toLowerCase();
    return (this.args.languages ?? []).filter(lang => lang && lang.code && !lang.hidden).map(lang => {
      const code = String(lang.code);
      const short = code.slice(0, 2).toLowerCase();
      const meta = LANGS[short] ?? {
        name: code,
        flag: null
      };
      const isCurrent = current.length === 2 ? current === short : current !== '' && current === code.toLowerCase();
      return {
        code,
        short: short.toUpperCase(),
        name: meta.name,
        flagUrl: meta.flag ? `${FLAG_BASE}${meta.flag}.png` : null,
        hooks: String(lang.hooks ?? '').split(/\s+/).filter(Boolean),
        isCurrent
      };
    });
  }
  get currentItem() {
    return this.items.find(item => item.isCurrent) ?? null;
  }
  get triggerLabel() {
    const label = this.args.label ?? '';
    const name = this.currentItem?.name;
    return name ? `${label}: ${name}` : label;
  }
  setRoot(element) {
    this._root = element;
  }

  // the classic data-test hooks of each row: static per code, written once
  markItem(item, element) {
    for (const hook of item.hooks) element.setAttribute(`data-test-${hook}`, item.code);
  }
  toggle() {
    if (this.isOpen) {
      this.close(true);
    } else {
      this.open('current');
    }
  }
  open(focus) {
    this.isOpen = true;
    this._listenOutside();
    schedule('afterRender', this, this._afterOpen, focus);
  }
  close(returnFocus = false) {
    this.isOpen = false;
    this._unlistenOutside();
    if (returnFocus === true) this._root?.querySelector('.nv-lang-menu-trigger')?.focus();
  }
  pick(code) {
    this.close(true);
    this.args.onPick?.(code);
  }
  onTriggerKey(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) this.open(event.key === 'ArrowUp' ? 'last' : 'current');else this._focusRow(event.key === 'ArrowUp' ? 'last' : 'first');
    }
  }
  onMenuKey(event) {
    const key = event.key;
    if (key === 'Escape') {
      // the open menu owns Escape: the drawer / panel under it stays open
      event.preventDefault();
      event.stopPropagation();
      this.close(true);
    } else if (key === 'Tab') {
      this.close(false);
    } else if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      this._focusRow(key === 'ArrowDown' ? 'next' : 'prev');
    } else if (key === 'Home' || key === 'End') {
      event.preventDefault();
      this._focusRow(key === 'Home' ? 'first' : 'last');
    }
  }
  _rows() {
    return Array.from(this._root?.querySelectorAll('.nv-lang-menu-item') ?? []);
  }
  _focusRow(where) {
    const rows = this._rows();
    if (!rows.length) return;
    const at = rows.indexOf(document.activeElement);
    let index = 0;
    if (where === 'last') index = rows.length - 1;else if (where === 'current') index = Math.max(0, rows.findIndex(row => row.getAttribute('aria-checked') === 'true'));else if (where === 'next') index = at < 0 ? 0 : (at + 1) % rows.length;else if (where === 'prev') index = at < 0 ? rows.length - 1 : (at - 1 + rows.length) % rows.length;
    rows[index]?.focus();
  }
  _afterOpen(focus) {
    if (!this.isOpen || this.isDestroying || this.isDestroyed) return;
    this._fit();
    this._focusRow(focus);
  }

  // a floating pane that would leave the window opens to the other side (class on the element, not tracked state)
  _fit() {
    const pop = this._root?.querySelector('.nv-lang-menu-pop');
    if (!pop || this.args.inline) return;
    pop.classList.remove('is-up', 'is-start');
    const rect = pop.getBoundingClientRect();
    if (rect.left < 8) pop.classList.add('is-start');
    const trigger = this._root.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - 8 && trigger.top > rect.height + 8) pop.classList.add('is-up');
  }
  _listenOutside() {
    if (this._onOutside) return;
    this._onOutside = event => {
      if (this._root && !this._root.contains(event.target)) this.close(false);
    };
    // pointerdown for touch / pen, mousedown for the mouse and for the test helpers (they fire no pointer events)
    for (const type of OUTSIDE_EVENTS) document.addEventListener(type, this._onOutside, true);
  }
  _unlistenOutside() {
    if (!this._onOutside) return;
    for (const type of OUTSIDE_EVENTS) document.removeEventListener(type, this._onOutside, true);
    this._onOutside = null;
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this._unlistenOutside();
    this._root = null;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "isOpen", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "setRoot", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setRoot"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "markItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "markItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggle", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "close", [action], Object.getOwnPropertyDescriptor(_class.prototype, "close"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "pick", [action], Object.getOwnPropertyDescriptor(_class.prototype, "pick"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onTriggerKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onTriggerKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onMenuKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onMenuKey"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, NvLangMenuComponent);

export { NvLangMenuComponent as default };
//# sourceMappingURL=nv-lang-menu.js.map
